/**
 * 取組の「現在地」を、文章ではなく 数字 → 目標 → 目標までの進捗 で出す。
 * 目標が決まっていない指標は「目標 未設定」と出す。勝手に目標を決めない。
 *
 * 指標の中身・ラベル・目標値は deploy 供給（/api/meeting-board/metrics）が持つ。
 * ここは並べ方と達成度の色分けだけを担当する。
 */
import { useBoardSection } from '@/hooks/useMeetingBoard'
import { cn } from '@/lib/utils'

export interface TabMetric {
  label: string
  value: string
  target: string | null
  progressPct?: number
  note?: string
}

export interface LandingRow {
  month: string
  n: number
  activated: number
  cancelled: number
  currentPct: number
  weak: number
  strong: number
  gapPt: number
  needCases: number
  verdict: 'reach' | 'maybe' | 'short'
  shortPt: number
}

export interface LandingTable {
  target: number
  note?: string
  rows: LandingRow[]
}

export interface PendingTable {
  total: number
  stateLabels: Record<string, string>
  note?: string
  rows: ({ month: string; count: number } & Record<string, number | string>)[]
}

export interface TabMetrics {
  tab: string
  asOf: string | null
  sourceLabel: string
  metrics: TabMetric[]
  plannedMetrics?: string[]
  landingTable?: LandingTable
  pendingTable?: PendingTable
}

export function useTabMetrics(tab: string) {
  return useBoardSection<TabMetrics>('metrics', tab)
}

/** 達成度の色分け。半分未満は赤、届いていなければ黄、達していれば緑 */
function toneOf(percent: number): { bar: string; text: string } {
  if (percent >= 100) return { bar: 'bg-green-600', text: 'text-green-600' }
  if (percent >= 50) return { bar: 'bg-yellow-600', text: 'text-yellow-600' }
  return { bar: 'bg-destructive', text: 'text-destructive' }
}

export function BoardMetricGrid({ metrics }: { metrics: TabMetric[] }) {
  if (metrics.length === 0) return null
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {metrics.map((metric) => {
        const percent = metric.progressPct
        const tone = percent === undefined ? null : toneOf(percent)
        return (
          <div key={metric.label} className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{metric.label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{metric.value}</p>

            <div className="mt-2 flex items-baseline justify-between gap-2 text-xs">
              <span className={cn('tabular-nums', metric.target ? 'text-muted-foreground' : 'text-yellow-600')}>
                {metric.target ? `目標 ${metric.target}` : '目標 未設定'}
              </span>
              {percent !== undefined && tone && (
                <span className={cn('font-medium tabular-nums', tone.text)}>{percent}%</span>
              )}
            </div>

            {percent !== undefined && tone && (
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted" role="presentation">
                <div className={cn('h-full rounded-full', tone.bar)} style={{ width: `${Math.min(100, percent)}%` }} />
              </div>
            )}

            {metric.note && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{metric.note}</p>}
          </div>
        )
      })}
    </div>
  )
}

/**
 * コンサルの判断はついているのに、まだ開通もキャンセルもしていない案件（2026-08-09 沖中指示）。
 * 「何月投入分が何件あるか」と、どこで止まっているかの内訳を出す。人手を入れる対象がここ。
 *
 * スマホでは表にせずカードで縦積みにする（横スクロールさせない）。
 */
export function BoardPendingTable({ table }: { table: PendingTable }) {
  if (table.rows.length === 0) return null
  const states = Object.entries(table.stateLabels)
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">コンサル判断済み・未決着の案件</h3>
        <span className="text-xs tabular-nums text-muted-foreground">合計 {table.total.toLocaleString()}件</span>
      </div>

      {/* スマホ: カード */}
      <div className="grid gap-3 md:hidden">
        {table.rows.map((row) => (
          <div key={row.month} className="rounded-lg border bg-card p-4">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium tabular-nums">{row.month} 投入</span>
              <span className="text-xl font-semibold tabular-nums">{row.count.toLocaleString()}件</span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
              {states.map(([key, label]) => (
                <div key={key} className="flex items-baseline justify-between gap-2 rounded-md bg-muted/60 px-2 py-1">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="font-medium tabular-nums">{Number(row[key] ?? 0).toLocaleString()}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {/* デスクトップ: 表 */}
      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">投入月</th>
              <th className="px-4 py-2 font-medium">未決着</th>
              {states.map(([key, label]) => (
                <th key={key} className="px-4 py-2 font-medium">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {table.rows.map((row) => (
              <tr key={row.month}>
                <td className="px-4 py-3 font-medium tabular-nums">{row.month}</td>
                <td className="px-4 py-3 text-base font-semibold tabular-nums">{row.count.toLocaleString()}件</td>
                {states.map(([key]) => (
                  <td key={key} className="px-4 py-3 tabular-nums text-muted-foreground">
                    {Number(row[key] ?? 0).toLocaleString()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {table.note && <p className="text-xs text-muted-foreground">{table.note}</p>}
    </section>
  )
}

const VERDICT: Record<LandingRow['verdict'], { label: string; className: string }> = {
  reach: { label: '届く見込み', className: 'text-green-600' },
  maybe: { label: '強気なら届く', className: 'text-yellow-600' },
  short: { label: '届かない見込み', className: 'text-destructive' },
}

/**
 * 投入月ごとの開通率と着地予測（2026-08-09 沖中指示）。
 * 「今どこまで来ていて、目標まであと何ポイント／何件で、着地はどこに落ちるか」を1行で読む。
 */
export function BoardLandingTable({ table }: { table: LandingTable }) {
  if (table.rows.length === 0) return null
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">投入月ごとの開通率と着地予測</h3>
        <span className="text-xs tabular-nums text-muted-foreground">目標 {table.target}%</span>
      </div>

      {/* スマホ: カード（横スクロールさせない） */}
      <div className="grid gap-3 md:hidden">
        {table.rows.map((row) => {
          const verdict = VERDICT[row.verdict]
          return (
            <div key={row.month} className="rounded-lg border bg-card p-4">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium tabular-nums">{row.month} 投入</span>
                <span className="text-xs tabular-nums text-muted-foreground">{row.n.toLocaleString()}件</span>
              </div>
              <p className="mt-2 text-2xl font-semibold tabular-nums">{row.weak}〜{row.strong}%</p>
              <p className="text-xs text-muted-foreground">着地予測（弱気〜強気）</p>
              <dl className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">今の開通率</dt>
                  <dd className="tabular-nums">{row.currentPct}%（{row.activated}件）</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">目標まで</dt>
                  <dd className="tabular-nums">あと {row.gapPt}pt ＝ {row.needCases.toLocaleString()}件</dd>
                </div>
              </dl>
              <p className={cn('mt-2 text-sm font-medium', verdict.className)}>
                {verdict.label}
                {row.verdict !== 'reach' && <span className="ml-2 text-xs tabular-nums">強気でも -{row.shortPt}pt</span>}
              </p>
            </div>
          )
        })}
      </div>

      {/* デスクトップ: 表 */}
      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">投入月</th>
              <th className="px-4 py-2 font-medium">投入数</th>
              <th className="px-4 py-2 font-medium">今の開通率</th>
              <th className="px-4 py-2 font-medium">目標まで</th>
              <th className="px-4 py-2 font-medium">着地予測（弱気〜強気）</th>
              <th className="px-4 py-2 font-medium">目標に届くか</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {table.rows.map((row) => {
              const verdict = VERDICT[row.verdict]
              return (
                <tr key={row.month}>
                  <td className="px-4 py-3 font-medium tabular-nums">{row.month}</td>
                  <td className="px-4 py-3 tabular-nums">{row.n.toLocaleString()}件</td>
                  <td className="px-4 py-3 tabular-nums">
                    {row.currentPct}%
                    <span className="ml-2 text-xs text-muted-foreground">開通 {row.activated}件</span>
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    あと {row.gapPt}pt
                    <span className="ml-2 text-xs text-muted-foreground">＝{row.needCases.toLocaleString()}件</span>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{row.weak}〜{row.strong}%</td>
                  <td className={cn('px-4 py-3 font-medium', verdict.className)}>
                    {verdict.label}
                    {row.verdict !== 'reach' && (
                      <span className="ml-2 text-xs tabular-nums">強気でも -{row.shortPt}pt</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {table.note && <p className="text-xs text-muted-foreground">{table.note}</p>}
    </section>
  )
}

/** 日報がまだ配信されていない取組向け。入る予定の数字だけ見せる */
export function BoardPlannedMetrics({ items }: { items: string[] }) {
  if (items.length === 0) return null
  return (
    <div className="rounded-lg border border-dashed p-4">
      <p className="text-xs font-medium text-muted-foreground">配信が始まると、ここに入る数字</p>
      <ul className="mt-2 space-y-1">
        {items.map((item) => (
          <li key={item} className="text-sm text-muted-foreground">・{item}</li>
        ))}
      </ul>
    </div>
  )
}
